import React, { useState, useEffect } from "react";
import { X, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { OFFICES } from "@/data/sampleListings";
import { CLAIM_MODAL } from "@/constants/testIds";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyState = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  office: "KW Wellington",
  hasSigns: false,
  isCertified: false,
  ackClaimEarly: false,
  ackContactAgent: false,
  ackKWWellington: false,
  ackSelfReport: false,
};

export const ClaimModal = ({ open, onOpenChange, listing, onConfirm }) => {
  const [form, setForm] = useState(emptyState);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(emptyState);
  }, [open, listing?.id]);

  if (!listing) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const requiredAgreements =
    form.ackClaimEarly &&
    form.ackContactAgent &&
    form.ackKWWellington;

  const canSubmit =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.phone.trim() &&
    form.email.trim() &&
    form.office &&
    requiredAgreements;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    const claimedBy = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      office: form.office,
      hasSigns: form.hasSigns,
      isCertified: form.isCertified,
      claimedAt: new Date().toISOString(),
    };

    // Step 1 — persist the claim on the backend. If this fails we bail out; we
    // don't want to fire a notification email for a claim that isn't recorded.
    try {
      await onConfirm(listing.id, claimedBy);
    } catch (err) {
      console.error("claim persist failed:", err?.message || err);
      if (err?.status === 409) {
        toast.warning("Already claimed", {
          description: `${listing.address} was claimed by another agent while you were filling out the form.`,
        });
      } else {
        toast.error("Couldn't claim this opportunity", {
          description: "Please refresh and try again.",
        });
      }
      setSubmitting(false);
      return;
    }

    // Step 2 — send the notification email to the listing agent.
    let emailOk = true;
    try {
      const resp = await fetch(`${API}/notify-claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listing_agent_email: listing.agentEmail,
          listing_agent_name: listing.agentName,
          property_address: listing.address,
          property_city: listing.city,
          available_date: formatDate(listing.availableDate),
          seller_pref_time: listing.sellerPrefTime || null,
          claiming_agent_first_name: claimedBy.firstName,
          claiming_agent_last_name: claimedBy.lastName,
          claiming_agent_phone: claimedBy.phone,
          claiming_agent_email: claimedBy.email,
          claiming_agent_office: claimedBy.office,
        }),
      });
      if (!resp.ok) emailOk = false;
    } catch (err) {
      emailOk = false;
      console.error("notify-claim failed:", err?.message || err);
    }

    if (emailOk) {
      toast.success("Opportunity claimed", {
        description: `${listing.agentName} has been notified by email with your contact info.`,
        icon: <CheckCircle2 className="text-[color:var(--kw-teal)]" size={18} />,
      });
    } else {
      toast.warning("Claimed — but email couldn't be delivered", {
        description: `Reach ${listing.agentName} directly at ${listing.agentPhone} or ${listing.agentEmail}.`,
      });
    }

    setSubmitting(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={CLAIM_MODAL.root}
        className="max-w-3xl p-0 gap-0 max-h-[92vh] overflow-hidden bg-white border-0 rounded-3xl shadow-2xl [&>button]:hidden"
      >
        <div className="kw-red-bar h-1 w-full" />

        <DialogHeader className="px-8 py-5 border-b border-[color:var(--kw-border)] flex-row items-center justify-between space-y-0">
          <DialogTitle className="font-display text-2xl md:text-3xl font-black tracking-tight text-[color:var(--kw-charcoal)] uppercase">
            Claim Opportunity
          </DialogTitle>
          <DialogClose
            data-testid={CLAIM_MODAL.closeBtn}
            className="text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-charcoal)] transition-colors"
          >
            <X size={22} strokeWidth={2.5} />
          </DialogClose>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto kw-scroll px-8 py-6 space-y-5"
          style={{ maxHeight: "calc(92vh - 80px)" }}
        >
          {/* Property banner (teal) */}
          <div
            className="rounded-2xl px-7 py-6 text-white"
            style={{
              background:
                "linear-gradient(135deg, var(--kw-teal) 0%, var(--kw-teal-dark) 100%)",
              boxShadow: "0 20px 40px -20px rgba(31, 138, 122, 0.5)",
            }}
          >
            <div
              className="text-xs font-bold tracking-[0.18em] uppercase"
              style={{ color: "rgba(255,255,255,0.85)" }}
            >
              Property Information
            </div>
            <div className="font-display text-2xl md:text-3xl font-black leading-tight mt-1">
              {listing.address}
            </div>
            <div className="font-bold tracking-widest uppercase text-sm mt-1" style={{ color: "rgba(255,255,255,0.92)" }}>
              {formatDate(listing.availableDate)}
            </div>
          </div>

          {/* Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-caps block mb-1.5">First Name *</label>
              <input
                data-testid={CLAIM_MODAL.fieldFirstName}
                className="input-kw"
                value={form.firstName}
                onChange={(e) => set("firstName", e.target.value)}
                required
              />
            </div>
            <div>
              <label className="label-caps block mb-1.5">Last Name *</label>
              <input
                data-testid={CLAIM_MODAL.fieldLastName}
                className="input-kw"
                value={form.lastName}
                onChange={(e) => set("lastName", e.target.value)}
                required
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-caps block mb-1.5">Phone Number *</label>
              <input
                data-testid={CLAIM_MODAL.fieldPhone}
                type="tel"
                className="input-kw"
                placeholder="561-555-0123"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                required
              />
            </div>
            <div>
              <label className="label-caps block mb-1.5">Email Address *</label>
              <input
                data-testid={CLAIM_MODAL.fieldEmail}
                type="email"
                className="input-kw"
                placeholder="agent@kwwellington.com"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                required
              />
            </div>
          </div>

          {/* Office */}
          <div>
            <label className="label-caps block mb-1.5">Select Your Office *</label>
            <select
              data-testid={CLAIM_MODAL.fieldOffice}
              className="input-kw appearance-none bg-white"
              value={form.office}
              onChange={(e) => set("office", e.target.value)}
              required
            >
              {OFFICES.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          {/* Status & Certification (Optional) */}
          <div className="pt-3">
            <div className="flex items-center gap-3 mb-2">
              <h4 className="font-display font-black uppercase tracking-wider text-sm text-[color:var(--kw-charcoal)]">
                Status &amp; Certification
              </h4>
              <span className="text-[10px] font-semibold tracking-widest uppercase text-[color:var(--muted-foreground)]">
                Optional
              </span>
            </div>
            <div className="space-y-0 divide-y divide-[color:var(--kw-border)]/60">
              <label data-checked={form.hasSigns} className="checkbox-row">
                <input
                  data-testid={CLAIM_MODAL.checkSigns}
                  type="checkbox"
                  checked={form.hasSigns}
                  onChange={(e) => set("hasSigns", e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[color:var(--kw-red)]"
                />
                <span className="text-xs font-bold tracking-wide uppercase leading-relaxed text-[color:var(--kw-charcoal-soft)]">
                  I have open house signs
                </span>
              </label>
              <label data-checked={form.isCertified} className="checkbox-row">
                <input
                  data-testid={CLAIM_MODAL.checkCertified}
                  type="checkbox"
                  checked={form.isCertified}
                  onChange={(e) => set("isCertified", e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[color:var(--kw-red)]"
                />
                <span className="text-xs font-bold tracking-wide uppercase leading-relaxed text-[color:var(--kw-charcoal-soft)]">
                  I am &ldquo;KWW Open House Certified&rdquo;
                </span>
              </label>
            </div>
          </div>

          {/* Agreement Checklist (Required) */}
          <div className="pt-3">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 className="text-[color:var(--kw-red)]" size={16} strokeWidth={2.5} />
              <h4 className="font-display font-black uppercase tracking-wider text-sm text-[color:var(--kw-red)]">
                Agreement Checklist
              </h4>
              <span className="text-[10px] font-semibold tracking-widest uppercase text-[color:var(--kw-red)]/80">
                Required
              </span>
            </div>

            <div className="space-y-0 divide-y divide-[color:var(--kw-border)]/60">
              <label data-checked={form.ackClaimEarly} className="checkbox-row bg-white">
                <input
                  data-testid={CLAIM_MODAL.checkClaimEarly}
                  type="checkbox"
                  checked={form.ackClaimEarly}
                  onChange={(e) => set("ackClaimEarly", e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[color:var(--kw-red)]"
                />
                <span className="text-xs font-bold tracking-wide uppercase leading-relaxed text-[color:var(--kw-charcoal)]">
                  I acknowledge that I will claim the listing as early in the week as possible, not the night before, to allow enough time to drive exposure and foot traffic to the open house *
                </span>
              </label>

              <label data-checked={form.ackContactAgent} className="checkbox-row bg-white">
                <input
                  data-testid={CLAIM_MODAL.checkContactAgent}
                  type="checkbox"
                  checked={form.ackContactAgent}
                  onChange={(e) => set("ackContactAgent", e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[color:var(--kw-red)]"
                />
                <span className="text-xs font-bold tracking-wide uppercase leading-relaxed text-[color:var(--kw-charcoal)]">
                  I will contact the listing agent immediately after claiming the opportunity and understand that the listing agent has the right to say no if there&rsquo;s not enough time for it to be properly marketed. *
                </span>
              </label>

              <label data-checked={form.ackKWWellington} className="checkbox-row bg-white">
                <input
                  data-testid={CLAIM_MODAL.checkKWWellingtonAgent}
                  type="checkbox"
                  checked={form.ackKWWellington}
                  onChange={(e) => set("ackKWWellington", e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[color:var(--kw-red)]"
                />
                <span className="text-xs font-bold tracking-wide uppercase leading-relaxed text-[color:var(--kw-charcoal)]">
                  I agree that I am a KW Wellington agent and am not with another group/company. *
                </span>
              </label>

              <label data-checked={form.ackSelfReport} className="checkbox-row bg-white">
                <input
                  data-testid={CLAIM_MODAL.checkSelfReport}
                  type="checkbox"
                  checked={form.ackSelfReport}
                  onChange={(e) => set("ackSelfReport", e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[color:var(--kw-red)]"
                />
                <span className="text-xs font-bold tracking-wide uppercase leading-relaxed text-[color:var(--kw-red)]">
                  As a participant in the Agent Success Coaching Program, I agree to use the ASC Agents&rsquo; Self-Report Held Open Houses link at the top of the page to self-report the open houses I&rsquo;ve held.
                </span>
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="pt-2 pb-1">
            <button
              data-testid={CLAIM_MODAL.submitBtn}
              type="submit"
              disabled={!canSubmit || submitting}
              className={`w-full py-4 rounded-full text-base kw-btn-dark ${(!canSubmit || submitting) ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              {submitting ? "Notifying Listing Agent…" : "Confirm & Claim"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimModal;
